import React, { Dispatch } from "react";
import { connect } from "react-redux";
import { Container, Button, Input } from "reactstrap";
import { RouteComponentProps } from "react-router";
import { CMDataGrid, DataGridFilterBy } from "cm-data-grid";
import { showSnack } from "react-redux-snackbar";
import { User } from "oidc-client";
import { Icon } from "@cadmakers-ui/icons";
import { Topic, CreateTopic } from "../../Models/Topic";
import {
  IAppState,
  LoadingState,
  ResponseError,
  ErrorDisplay,
  LoadingDisplay,
  appPaths,
  ApiRequest,
  ForbiddenDisplay
} from "../../../Common";
import {
  addTopic,
  updateTopics,
  setActiveTopic,
  setActiveApp,
  getTopics
} from "../../../Startup/Actions/StartupActions";
import { Application } from "../../../App";
import { endpoints } from "../../Constants/endpoints";
import ResponseHandler from "../../../App/ResponseHandler";
import { ResponseResult } from "../../../Common/Models/ResponseResult";
import { StatusCode } from "../../../App/Enums/StatusCode";
import { Company } from "../../../Company/Models/Company";
import { CreateTopicModal } from "../CreateTopicModal";
import { FeedbackCategory, FeedbackParent } from "../../../Feedback/Constants/feedbackEnums";
import { ProtectedContent } from "../../../Auth";
import { Role } from "../../../Common/Constants/roleEnums";
import { ErrorHandling } from "../../../Auth/Components/ProtectedContent";
import { TopicTypeBadge } from "../TopicTypeBadge";
import { TopicsListItemExtra } from "./TopicsListItemExtra";
import { TopicListItemButtons } from "./TopicListItemButtons";
import styles from "../../Styles/TopicListContainer.module.scss";
import iconStyles from "../../../Common/Styles/Icons.module.scss";

type TParams = {
  shortname: string;
};

type State = {
  toggleCreateTopic: boolean;
  filter: string;
  topics: Topic[];
  topicsLoadingState: LoadingState;
  topicsLoadingError?: ResponseError;
  tableData: any[][];
};

type StateProps = {
  user?: User;
  company?: Company;
  companyLoadingState: LoadingState;
  companyLoadingError: ResponseError;
  activeTopic?: Topic;
  parentType: FeedbackParent;
  applications: Application[];
  appsLoadingState: LoadingState;
};

type DispatchProps = {
  getTopics: (shortname: string) => void;
  setLoadingTopics: () => void;
  addTopic: (topic: Topic) => void;
  showSnack: (uniqueId: any, args: any) => void;
  updateTopics: (topics: Topic[]) => void;
  setActiveTopic: (topic: Topic) => void;
  setActiveApp: (app: Application) => void;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  user: state.oidc.user,
  company: state.app.company,
  companyLoadingState: state.app.companyLoadingState,
  companyLoadingError: state.app.companyLoadingError,
  activeTopic: state.app.activeTopic,
  parentType: state.app.parentType,
  applications: state.app.applications,
  appsLoadingState: state.app.appsLoadingState
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  showSnack: (uniqueId: any, args: any) => dispatch(showSnack(uniqueId, args)),
  addTopic: (topic: Topic) => dispatch(addTopic(topic)),
  updateTopics: (topics: Topic[]) => dispatch(updateTopics(topics)),
  setActiveTopic: (topic: Topic) => dispatch(setActiveTopic(topic)),
  setActiveApp: (app: Application) => dispatch(setActiveApp(app)),
  getTopics: (shortname: string) => dispatch(getTopics(shortname))
});

const initalState = {
  toggleCreateTopic: false,
  filter: "",
  topicsLoadingState: {
    errored: false,
    isLoaded: false,
    isLoading: false
  },
  topics: [],
  tableData: []
};

class TopicsListContainer extends React.Component<
  StateProps & RouteComponentProps<TParams> & DispatchProps,
  State
> {
  topicCreateLock: boolean;

  constructor(props: StateProps & RouteComponentProps<TParams> & DispatchProps) {
    super(props);

    this.state = {
      ...initalState
    };

    this.topicCreateLock = false;
  }

  componentDidMount = () => {
    this.loadTopics();
  };

  componentDidUpdate = (prevProps: StateProps & RouteComponentProps<TParams> & DispatchProps) => {
    if (this.props.match.params.shortname !== prevProps.match.params.shortname) {
      this.setState({
        ...initalState
      });
    }

    this.loadTopics();
  };

  loadTopics = () => {
    if (
      !this.state.topicsLoadingState.isLoaded &&
      !this.state.topicsLoadingState.isLoading &&
      !this.state.topicsLoadingState.errored
    ) {
      this.getTopics();
    }
  };

  getTopics = () => {
    if (this.props.company) {
      this.setLoadingState(false, true, false, () => {
        ApiRequest.fromPath(
          endpoints.topics.getByCompany(this.props.company ? this.props.company.shortName : "")
        )
          .withAuthHeader()
          .get()
          .then(response => {
            ResponseHandler.handle(
              response,
              (result: ResponseResult<Topic[]>) => {
                this.setState(
                  {
                    topics: result.payload,
                    tableData: this.mapTopicsToTableData(result.payload)
                  },
                  () => {
                    this.setLoadingState(false, false, true, () => {
                      if (
                        !!this.props.company &&
                        this.props.match.params.shortname === this.props.company.shortName
                      ) {
                        this.props.updateTopics(this.state.topics);
                      }
                    });
                  }
                );
              },
              result => {
                this.setLoadingState(true, false, false);
                this.setError(response.status, "Failed to get topics", result.error.message);
              },
              () => {
                this.setLoadingState(true, false, false);
                this.setError(
                  response.status,
                  "Failed to get topics",
                  "An unknown error has occured"
                );
              }
            ).catch(() => {
              this.setLoadingState(true, false, false);
              this.setError(
                response.status,
                "Failed to get topics",
                "Failed to parse JSON response"
              );
            });
          })
          .catch(() => {
            this.setLoadingState(true, false, false);
            this.setError(
              StatusCode.Status503ServiceUnavailable,
              "Failed to get topics",
              "Unable to contact server"
            );
          });
      });
    }
  };

  onCreateTopic = (topic: CreateTopic, callback: () => void) => {
    if (this.props.company && this.claimTopicCreateLock()) {
      ApiRequest.fromPath(endpoints.topics.create(this.props.company.shortName))
        .withJson(topic)
        .withAuthHeader()
        .post()
        .then(response => {
          ResponseHandler.handle(
            response,
            (result: ResponseResult<Topic>) => {
              callback();
              const newTopics = [...this.state.topics, result.payload];
              this.setState((prevState: State) => ({
                topics: newTopics,
                tableData: this.mapTopicsToTableData(newTopics)
              }));

              if (this.props.company) {
                this.props.addTopic(result.payload);
              }

              this.releaseTopicCreateLock();
            },
            result => {
              this.showErrorSnackbar(
                "Failed to create topic",
                result.error.message,
                response.status
              );

              this.releaseTopicCreateLock();
            },
            () => {
              this.showErrorSnackbar(
                "Failed to create topic",
                "An unknown error has occured",
                response.status
              );

              this.releaseTopicCreateLock();
            }
          );
        })
        .catch(() => {
          this.showErrorSnackbar(
            "Failed to create topic",
            "Unable to contact server",
            StatusCode.Status503ServiceUnavailable
          );
        });
    }
  };

  onArchiveTopic = (topic: Topic) => {
    ApiRequest.fromPath(endpoints.topics.archive(topic.code))
      .withAuthHeader()
      .delete()
      .then(response => {
        ResponseHandler.handle(
          response,
          () => {
            // for some reason lint thinks we are in a setState here
            // eslint-disable-next-line react/no-access-state-in-setstate
            const filteredTopics = [...this.state.topics].filter(t => t.code !== topic.code);

            if (
              !!this.props.company &&
              this.props.company.shortName === this.props.match.params.shortname
            ) {
              this.props.updateTopics(filteredTopics);

              if (filteredTopics.length === 0) {
                if (this.props.applications.length === 0) {
                  throw new Error(
                    "You should not be on this website with no applications and topics"
                  );
                }
                this.props.setActiveApp(this.props.applications[0]);
              } else {
                this.props.setActiveTopic(filteredTopics[0]);
              }
            }

            const updateCountIncTopics = filteredTopics.map(f => ({
              ...f,
              updateCount: f.updateCount ? f.updateCount + 1 : 1
            }));

            this.setState({
              topics: updateCountIncTopics,
              tableData: this.mapTopicsToTableData(updateCountIncTopics)
            });
          },
          result => {
            this.showErrorSnackbar(
              "Failed to archive topic",
              result.error.message,
              response.status
            );
          },
          () => {
            this.showErrorSnackbar(
              "Failed to archive topic",
              "An unknown error has occured",
              response.status
            );
          }
        );
      })
      .catch(() => {
        this.showErrorSnackbar(
          "Failed to archive topic",
          "Unable to contact server",
          StatusCode.Status503ServiceUnavailable
        );
      });
  };

  onEditTopic = (topic: Topic, callback: () => void) => {
    ApiRequest.fromPath(endpoints.topics.edit(topic.code))
      .withAuthHeader()
      .withJson(topic)
      .put()
      .then(response => {
        ResponseHandler.handle(
          response,
          () => {
            const editIndex = this.state.topics.findIndex(t => t.code === topic.code);

            // for some reason lint thinks we are in a setState here
            // eslint-disable-next-line react/no-access-state-in-setstate
            const editedTopics = [...this.state.topics];

            editedTopics[editIndex].name = topic.name;
            editedTopics[editIndex].description = topic.description;
            editedTopics[editIndex].type = topic.type;
            editedTopics[editIndex].workflowCode = topic.workflowCode;
            editedTopics[editIndex].updateCount = editedTopics[editIndex].updateCount
              ? editedTopics[editIndex].updateCount + 1
              : 1;

            if (
              !!this.props.company &&
              this.props.company.shortName === this.props.match.params.shortname
            ) {
              if (editIndex !== -1) {
                this.props.updateTopics(editedTopics);
              }
            }

            this.setState({
              topics: editedTopics,
              tableData: this.mapTopicsToTableData(editedTopics)
            });

            callback();
          },
          result => {
            this.showErrorSnackbar("Failed to edit topic", result.error.message, response.status);
          },
          () => {
            this.showErrorSnackbar(
              "Failed to edit topic",
              "An unknown error has occured",
              response.status
            );
          }
        );
      })
      .catch(() => {
        this.showErrorSnackbar(
          "Failed to edit topic",
          "Unable to contact server",
          StatusCode.Status503ServiceUnavailable
        );
      });
  };

  setLoadingState = (
    errored: boolean,
    isLoading: boolean,
    isLoaded: boolean,
    callback?: () => void
  ) => {
    this.setState(
      {
        topicsLoadingState: {
          errored,
          isLoading,
          isLoaded
        }
      },
      () => {
        if (callback) {
          callback();
        }
      }
    );
  };

  setError = (errorCode: number, errorReason: string, errorDescription: string) => {
    this.setState({
      topicsLoadingError: {
        errorCode,
        errorDescription,
        errorResult: errorReason
      }
    });
  };

  toggleCreateModal = () => {
    this.setState(prevState => ({
      toggleCreateTopic: !prevState.toggleCreateTopic
    }));
  };

  onGoToWorkflowsClick = () => {
    this.props.history.push(appPaths.topic.workflowTemplates);
  };

  onEditWorkflowClick = (topic: Topic) => {
    this.props.history.push(appPaths.workflows.edit.replace(":code", topic.workflowCode));
  };

  showErrorSnackbar = (reason: string, errorDescription: string, errorCode?: number) => {
    let label = `${reason}: ${errorDescription}`;

    if (errorCode) {
      label += `(Error Code ${errorCode})`;
    }

    this.props.showSnack("COMMENT_BOX", {
      label,
      timeout: 5000
    });
  };

  redirectToTopicFeedback = (topic: Topic) => {
    this.props.history.push(appPaths.topic.featureRequests.replace(":topicCode", topic.code));
  };

  onFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      filter: event.currentTarget.value
    });
  };

  filterTopics = () => {
    return this.state.filter.length > 0
      ? this.state.topics.filter(topic =>
          topic.name.toLowerCase().includes(this.state.filter.toLowerCase())
        )
      : this.state.topics;
  };

  claimTopicCreateLock(): boolean {
    if (this.topicCreateLock) {
      return false;
    }
    this.topicCreateLock = true;
    return true;
  }

  releaseTopicCreateLock() {
    this.topicCreateLock = false;
  }

  mapTopicsToTableData(topics: Topic[]) {
    return topics.map(el => {
      return [
        {
          displayValue: (
            <Icon
              appName="cmFeedback"
              assetName="SIDEBAR_L_Topics"
              className={iconStyles.smallIcon}
            />
          ),
          value: "",
          key: el.updateCount
        },
        {
          displayValue: <div className={styles.topicNameContainer}>{el.name}</div>,
          value: el.name,
          key: el.updateCount
        },
        {
          displayValue: <div className={styles.topicDescriptionContainer}>{el.description}</div>,
          value: el.description,
          key: el.updateCount
        },
        {
          displayValue: <TopicTypeBadge topic={el} />,
          value: el.type,
          key: el.updateCount
        },
        {
          displayValue: (
            <TopicsListItemExtra
              icon={
                <Icon
                  appName="cmBuilder"
                  assetName="VIEWER_L_Utilities_Lighting"
                  className={iconStyles.smallIcon}
                />
              }
              title="Feature Requests"
              data={el.featureRequestCount}
            />
          ),
          value: el.featureRequestCount,
          key: el.updateCount
        },
        {
          displayValue: (
            <TopicListItemButtons
              topic={el}
              onArchive={this.onArchiveTopic}
              onEdit={this.onEditTopic}
              onEditWorkflow={this.onEditWorkflowClick}
            />
          ),
          value: "",
          key: el.updateCount
        }
      ];
    });
  }

  render() {
    if (this.state.topicsLoadingState.errored) {
      if (
        !!this.state.topicsLoadingError &&
        this.state.topicsLoadingError.errorCode === StatusCode.Status403Forbidden
      ) {
        return <ForbiddenDisplay description={this.state.topicsLoadingError.errorDescription} />;
      }
      return <ErrorDisplay {...this.state.topicsLoadingError} />;
    }

    if (
      this.state.topicsLoadingState.isLoaded &&
      this.props.companyLoadingState.isLoaded &&
      this.props.appsLoadingState.isLoaded
    ) {
      return (
        <Container fluid>
          <h2>All Topics</h2>
          <br />
          <div>
            <ProtectedContent
              roles={[Role.CMAdmin, Role.Admin]}
              errorHandling={ErrorHandling.ShowNothing}
            >
              <Button color="primary" onClick={this.toggleCreateModal}>
                Create Topic
              </Button>
              &nbsp; &nbsp;
              <ProtectedContent roles={[Role.Admin]} errorHandling={ErrorHandling.ShowNothing}>
                <Button color="secondary" onClick={this.onGoToWorkflowsClick}>
                  Topic Workflow Templates
                </Button>
              </ProtectedContent>
              <CreateTopicModal
                toggle={this.state.toggleCreateTopic}
                onToggle={this.toggleCreateModal}
                onCreate={this.onCreateTopic}
              />
            </ProtectedContent>
            {/* <br />
            <br />
            <Input
              type="text"
              value={this.state.filter}
              onChange={this.onFilterChange}
              placeholder="Search topics"
              autoComplete="off"
            /> */}
          </div>

          <div style={{ marginTop: "10px" }}>
            {/* <TopicsList
              topics={this.filterTopics()}
              onClick={this.redirectToTopicFeedback}
              onItemArchive={this.onArchiveTopic}
              onItemEdit={this.onEditTopic}
              onItemEditWorkflow={this.onEditWorkflowClick}
            /> */}
            <CMDataGrid
              exportXls
              exportPdf
              changeableView
              selectableRow={false}
              // pageLength={maxPerPage}
              paging={false}
              resizeConfigs={{
                resizable: false,
                options: {
                  liveDrag: true,
                  disabledColumns: []
                },
                serialize: false
              }}
              columns={[
                {
                  // topic icon
                  name: "",
                  searchable: false,
                  sortable: false
                },
                {
                  name: "Name",
                  searchable: true,
                  sortable: true,
                  filterable: { by: DataGridFilterBy.StartsWith }
                },
                {
                  name: "Description",
                  searchable: true,
                  sortable: false,
                  filterable: { by: DataGridFilterBy.StartsWith }
                },
                {
                  name: "Type",
                  searchable: false,
                  sortable: true,
                  filterable: { by: DataGridFilterBy.ExistingValues }
                },
                {
                  name: "Details",
                  searchable: false,
                  sortable: true // re-enable once search by number value works in Datagrid
                },
                {
                  // buttons
                  name: "",
                  searchable: false,
                  sortable: false
                }
              ]}
              // mobileConfig={{
              //   header: 1,
              //   description: 7,
              //   // image: 0,
              //   info: 2,
              //   icon: 3
              // }}
              useTiles={false}
              data={this.state.tableData}
              onClickItem={(e: any, index: number) => {
                this.redirectToTopicFeedback(this.state.topics[index]);
              }}
            />
          </div>
        </Container>
      );
    }
    return <LoadingDisplay />;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TopicsListContainer);
